import type { Theme } from '../../types';

/** Libellés français des tags de thèmes Lichess retenus par
 * scripts/build-puzzles.mjs — indépendant de `Theme` (thèmes de gaffes
 * détectées en partie, cf. coaching/themes.ts), taxonomie différente. */
export const PUZZLE_THEME_LABELS: Record<string, string> = {
  mateIn1: 'Mat en 1',
  mateIn2: 'Mat en 2',
  fork: 'Fourchette',
  pin: 'Clouage',
  skewer: 'Enfilade',
  hangingPiece: 'Pièce en prise',
  discoveredAttack: 'Attaque à la découverte',
  backRankMate: 'Mat du couloir',
  trappedPiece: 'Pièce piégée',
  deflection: 'Déviation',
  doubleCheck: 'Double échec',
  exposedKing: 'Roi exposé',
};

export function puzzleThemeLabel(tag: string): string {
  return PUZZLE_THEME_LABELS[tag] ?? tag;
}

/** Trie une liste de tags en mettant d'abord ceux qu'on sait traduire (nos 12
 * thèmes ciblés) — un puzzle porte aussi des tags Lichess généraux
 * (« endgame », « master »…) sans intérêt pédagogique propre, à ne montrer
 * qu'en dernier recours. */
export function sortThemesByRelevance(tags: string[]): string[] {
  const known = tags.filter((t) => PUZZLE_THEME_LABELS[t]);
  const rest = tags.filter((t) => !PUZZLE_THEME_LABELS[t]);
  return [...known, ...rest];
}

/** Thèmes de puzzles Lichess pertinents pour un thème de gaffe détecté en
 * partie — sert à suggérer des puzzles ciblés sur la cause n°1 du
 * diagnostic (cf. progress/diagnostic.ts). Correspondance volontairement
 * large, pas une équivalence stricte : juste une suggestion pertinente. */
const WEAKNESS_TO_PUZZLE_THEMES: Partial<Record<Theme, string[]>> = {
  pièce_en_prise: ['hangingPiece', 'trappedPiece'],
  fourchette: ['fork'],
  clouage_exploité: ['pin', 'skewer'],
  mat_manqué: ['mateIn1', 'mateIn2', 'backRankMate'],
  mat_évitable: ['mateIn1', 'mateIn2', 'backRankMate'],
  sécurité_du_roi: ['exposedKing', 'doubleCheck'],
  perte_matérielle: ['hangingPiece', 'discoveredAttack', 'deflection'],
  coup_plus_précis_disponible: ['discoveredAttack', 'deflection'],
};

export function puzzleThemesForWeakness(themes: Theme[]): string[] {
  const set = new Set<string>();
  for (const t of themes) {
    for (const p of WEAKNESS_TO_PUZZLE_THEMES[t] ?? []) set.add(p);
  }
  return [...set];
}
